import type { BroadcastChannel, MessagingRate } from '~/types/broadcast-message'

type Cust = { email?: string | null; phone?: string | null; user_id?: string | null; country?: string | null }

export function channelDestination(channel: BroadcastChannel, c: Cust): string | null {
  if (channel === 'email') return c.email || null
  if (channel === 'push') return c.user_id || null
  return c.phone || null // sms + whatsapp
}

export function normalizePhone(raw?: string | null): string {
  return String(raw ?? '').replace(/\D/g, '')
}

// Canonical suppression key: phone channels match on digits only so a stored
// opt-out (e.g. Twilio STOP from '+9617...') matches a customer phone in any format.
export function normalizeContact(channel: BroadcastChannel, value: string | null | undefined): string {
  if (channel === 'sms' || channel === 'whatsapp') return normalizePhone(value)
  return String(value ?? '')
}

export function isEligible(channel: BroadcastChannel, c: Cust, suppressed: Set<string>): boolean {
  const dest = channelDestination(channel, c)
  if (!dest) return false
  return !suppressed.has(normalizeContact(channel, dest))
}

function rateFor(
  channel: BroadcastChannel,
  country: string | null,
  rates: MessagingRate[],
  category: string | null,
): number {
  const cat = channel === 'whatsapp' ? category : null
  const match = rates.find(r => r.channel === channel && r.country_code === country && (r.category ?? null) === cat)
  if (match) return match.unit_cost
  const def = rates.find(r => r.channel === channel && r.country_code == null && (r.category ?? null) === cat)
  return def?.unit_cost ?? 0
}

export function estimateCost(
  recipients: { country_code: string | null }[],
  channel: BroadcastChannel,
  rates: MessagingRate[],
  category: string | null,
): { byCountry: Record<string, { count: number; cost: number }>; total: number } {
  const byCountry: Record<string, { count: number; cost: number }> = {}
  let total = 0
  for (const r of recipients) {
    const key = r.country_code ?? 'unknown'
    const unit = rateFor(channel, r.country_code, rates, category)
    if (!byCountry[key]) byCountry[key] = { count: 0, cost: 0 }
    byCountry[key].count += 1
    byCountry[key].cost = +(byCountry[key].cost + unit).toFixed(6)
    total = +(total + unit).toFixed(6)
  }
  return { byCountry, total }
}
